// ./src/utils/api/DonationService.ts
//
// ROADMAP #204 — the host's gifts and their receipts. A gift is paid online (an account order) or recorded by hand (a
// cheque, cash); the donor is a CONTACT. Receipts are never edited (CRA): they are canceled, replaced, issued, emailed;
// the PDF is made and kept by the server. A refund (online gifts) is the account order's; a void (by hand) the gift's.
// OpResult-native; `total` on the list result. The API owns every rule.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";

// Mirrors eventsmagic-api boundary/donation.ts.
export const DONATION_STATUS_VALUES = ["pending", "received", "refunded", "void"] as const;
/** How a gift recorded by hand arrived (never `card` — card gifts come through the online rail). */
export const DONATION_BY_HAND_METHODS = ["cheque", "cash", "transfer", "terminal", "other"] as const;

const ERRORS = `errors { name errors warnings }`;

const DONATION_FIELDS = `
  id
  accountId
  causeId
  organizerProfileId
  eventId
  accountOrderId
  personId
  donorName
  donorEmail
  currencyCode
  amount
  advantageAmount
  advantageDescription
  eligibleAmount
  isAnonymous
  message
  messageIsPublic
  messageHidden
  donationStatus
  paymentMethod
  receivedAt
  wantsReceipt
  isTest
  recurringGiftId
  yearReceiptId
  donationNumber
  paymentId
  receipts { id receiptNumber receiptState issuedAt emailedAt }
  receiptsStillIssued
  status
  rights
  createdAt
  updatedAt
`;

const LIST_QUERY = gql`
  query DonationList($filter: DonationFilter, $params: PaginationAndSorting) {
    donationList(filter: $filter, params: $params) {
      code
      total
      data { ${DONATION_FIELDS} }
      ${ERRORS}
    }
  }
`;

const GET_QUERY = gql`
  query DonationGet($id: ID) {
    donationGet(id: $id) {
      code
      data { ${DONATION_FIELDS} }
      ${ERRORS}
    }
  }
`;

const RECORD_MUTATION = gql`
  mutation DonationRecordByHand($params: DonationRecordInput) {
    donationRecordByHand(params: $params) {
      code
      data { ${DONATION_FIELDS} }
      ${ERRORS}
    }
  }
`;

const UPDATE_MUTATION = gql`
  mutation DonationUpdate($where: DonationWhereInput, $params: DonationInput) {
    donationUpdate(where: $where, params: $params) {
      code
      data { ${DONATION_FIELDS} }
      ${ERRORS}
    }
  }
`;

const NUMBERING_QUERY = gql`
  query DonationNumbering {
    donationNumbering {
      code
      data { prefix nextNumber }
      errors { name errors warnings }
    }
  }
`;

const NUMBERING_SAVE = gql`
  mutation DonationNumberingSave($params: DonationNumberingInput) {
    donationNumberingSave(params: $params) {
      code
      data { prefix nextNumber }
      errors { name errors warnings }
    }
  }
`;

const VOID_MUTATION = gql`
  mutation DonationVoid($params: DonationVoidInput) {
    donationVoid(params: $params) {
      code
      data { ${DONATION_FIELDS} }
      ${ERRORS}
    }
  }
`;

const REFUND_MUTATION = gql`
  mutation AccountOrderRefund($params: AccountOrderRefundInput) {
    accountOrderRefund(params: $params) {
      code
      data { orderId refundedAmount bypassed processor processorRefundId paymentStatus receiptsStillIssued }
      ${ERRORS}
    }
  }
`;

const RECEIPT_FIELDS = `
  id
  donationId
  regime
  serialNumber
  receiptNumber
  issuedAt
  giftDate
  currencyCode
  amount
  eligibleAmount
  receiptState
  canceledAt
  cancelReason
  replacesReceiptId
  emailedAt
  isTest
`;

const RECEIPT_PDF_MUTATION = gql`
  mutation DonationReceiptPdf($params: DonationReceiptIdInput) {
    donationReceiptPdf(params: $params) {
      code
      data { id fileUrl filename }
      ${ERRORS}
    }
  }
`;

const RECEIPT_EMAIL_MUTATION = gql`
  mutation DonationReceiptEmail($params: DonationReceiptIdInput) {
    donationReceiptEmail(params: $params) {
      code
      data { ${RECEIPT_FIELDS} }
      ${ERRORS}
    }
  }
`;

const RECEIPT_CANCEL_MUTATION = gql`
  mutation DonationReceiptCancel($params: DonationReceiptCancelInput) {
    donationReceiptCancel(params: $params) {
      code
      data { ${RECEIPT_FIELDS} }
      ${ERRORS}
    }
  }
`;

const RECEIPT_REPLACE_MUTATION = gql`
  mutation DonationReceiptReplace($params: DonationReceiptCancelInput) {
    donationReceiptReplace(params: $params) {
      code
      data { ${RECEIPT_FIELDS} }
      ${ERRORS}
    }
  }
`;

const RECEIPT_ISSUE_MUTATION = gql`
  mutation DonationReceiptIssue($params: DonationReceiptIssueInput) {
    donationReceiptIssue(params: $params) {
      code
      data { ${RECEIPT_FIELDS} }
      ${ERRORS}
    }
  }
`;

export interface DonationReceiptSummary {
  id?: string;
  receiptNumber?: string;
  /** issued | canceled */
  receiptState?: string;
  issuedAt?: string | null;
  emailedAt?: string | null;
}

export interface Donation {
  id?: string;
  accountId?: string;
  causeId?: string;
  organizerProfileId?: string | null;
  eventId?: string | null;
  /** The online order that paid it; null = recorded by hand. */
  accountOrderId?: string | null;
  personId?: string;
  /** #204 — its number, unique across the account (the account's prefix + counter, or typed from another system). */
  donationNumber?: string | null;
  /** #204 step 6a — the monthly gift it is a month of (null = a one-time gift). */
  recurringGiftId?: string | null;
  /** #204 step 6a — the yearly summary receipt that covers it. */
  yearReceiptId?: string | null;
  /** The contact's name and email as they are now. */
  donorName?: string | null;
  donorEmail?: string | null;
  currencyCode?: string;
  amount?: number;
  advantageAmount?: number | null;
  /** #204 — what it received in return (copied from its cause when made). */
  advantageDescription?: string | null;
  eligibleAmount?: number | null;
  isAnonymous?: boolean;
  message?: string | null;
  /** #204 — the donor agreed to show the note publicly with the gift. */
  messageIsPublic?: boolean;
  /** #204 — the host took the note off the public pages. */
  messageHidden?: boolean;
  donationStatus?: string;
  paymentMethod?: string | null;
  receivedAt?: string | null;
  wantsReceipt?: boolean;
  isTest?: boolean;
  paymentId?: string | null;
  receipts?: DonationReceiptSummary[];
  /** On a void: the receipts still issued (the host's to cancel). */
  receiptsStillIssued?: string[] | null;
  status?: number;
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface DonationFilter {
  id?: string[] | null;
  causeId?: string[] | null;
  eventId?: string[] | null;
  organizerProfileId?: string[] | null;
  personId?: string[] | null;
  /** #204 step 6a — the months of a monthly gift. */
  recurringGiftId?: string[] | null;
  donationStatus?: string[] | null;
  isTest?: boolean | null;
  /** The donor's name, email or phone. */
  searchKeyword?: string | null;
}

export interface DonationRecordInput {
  causeId: string;
  personId: string;
  amount: number;
  receivedAt?: string | null;
  paymentMethod?: string;
  eventId?: string | null;
  wantsReceipt?: boolean;
  isAnonymous?: boolean;
  message?: string | null;
  note?: string | null;
  emailDonor?: boolean;
  /** #204 — a number from another system; empty = the account's next. */
  donationNumber?: string | null;
  /** #204 — what it received in return; absent = the cause's (0 = nothing). */
  advantageAmount?: number | null;
  advantageDescription?: string | null;
}

export interface DonationUpdateInput {
  amount?: number;
  receivedAt?: string | null;
  paymentMethod?: string;
  wantsReceipt?: boolean;
  isAnonymous?: boolean;
  message?: string | null;
  /** #204 — take the donor's note off the public pages, or put it back. */
  messageHidden?: boolean;
  /** #204 — correct its number (unique across the account). */
  donationNumber?: string | null;
}

/** #204 — the account's automatic gift numbering. */
export interface DonationNumbering {
  /** Printed before every automatic number (null = none). */
  prefix: string | null;
  /** The number the next gift gets. */
  nextNumber: number;
}

export interface AccountOrderRefundInput {
  orderId: string;
  /** Default: everything left. */
  amount?: number | null;
  bypassProcessor?: boolean;
  externalRefundId?: string | null;
  note?: string | null;
}

/** The host's gifts — list, one, record by hand, edit, void, refund. */
export class DonationService {
  static async list(filter?: DonationFilter, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(LIST_QUERY, { filter: filter || {}, params: { pagination: pagination || undefined, sortBy: sortBy || undefined } });
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(GET_QUERY, { id });
  }

  static async recordByHand(params: DonationRecordInput): Promise<OpResult> {
    return ApiService.executeMutation(RECORD_MUTATION, { params });
  }

  static async update(id: string, params: DonationUpdateInput): Promise<OpResult> {
    return ApiService.executeMutation(UPDATE_MUTATION, { where: { id: [id] }, params });
  }

  /** #204 — the account's gift numbering (fresh: a card shows the next number). */
  static async numbering(): Promise<OpResult> {
    return ApiService.executeQuery(NUMBERING_QUERY, {}, { fetchPolicy: "network-only" });
  }

  /** #204 — save the prefix and/or where the counter continues (forward only). */
  static async saveNumbering(params: { prefix?: string | null; nextNumber?: number | null }): Promise<OpResult> {
    return ApiService.executeMutation(NUMBERING_SAVE, { params });
  }

  static async voidGift(id: string, reason?: string | null): Promise<OpResult> {
    return ApiService.executeMutation(VOID_MUTATION, { params: { id, reason: reason ?? null } });
  }

  static async refund(params: AccountOrderRefundInput): Promise<OpResult> {
    return ApiService.executeMutation(REFUND_MUTATION, { params });
  }
}

/** A gift's receipts — the PDF, email again, cancel, replace, issue. */
export class DonationReceiptService {
  static async pdf(id: string): Promise<OpResult> {
    return ApiService.executeMutation(RECEIPT_PDF_MUTATION, { params: { id } });
  }

  static async email(id: string): Promise<OpResult> {
    return ApiService.executeMutation(RECEIPT_EMAIL_MUTATION, { params: { id } });
  }

  static async cancel(id: string, reason: string | null, emailDonor: boolean): Promise<OpResult> {
    return ApiService.executeMutation(RECEIPT_CANCEL_MUTATION, { params: { id, reason, emailDonor } });
  }

  static async replace(id: string, reason: string | null, emailDonor: boolean): Promise<OpResult> {
    return ApiService.executeMutation(RECEIPT_REPLACE_MUTATION, { params: { id, reason, emailDonor } });
  }

  static async issue(donationId: string, emailDonor: boolean): Promise<OpResult> {
    return ApiService.executeMutation(RECEIPT_ISSUE_MUTATION, { params: { donationId, emailDonor } });
  }
}
